import { Case, CaseUpdateInput } from "./case";
import { ConditionType } from "./condition";
import { Partner } from "./state";
import { User, UserUpdate } from "./user";

export const GENERIC_SECTIONS = ["introduction", "user", "trip", "medical"] as const;

// Minimum questions answered required to reach checkout are 15 questions.
export const VALID_QUESTIONNAIRE_LENGTH = 15;

export type EntityType = "user" | "case";

type StorageMappingDefinition<T extends EntityType> = {
  propertyKey: T extends "user" ? keyof User : T extends "case" ? keyof Case : never;
};

export type StorageMapping<T extends EntityType> = {
  propertyKey: StorageMappingDefinition<T>["propertyKey"];
};

export type StoragePath<T extends EntityType> = (
  | {
      propertyKey: StorageMappingDefinition<T>["propertyKey"];
    }
  | {
      nestingProperty: PropertyKey;
      mappings: StoragePath<T>[];
    }
) & {
  // We can add other shared properties here in the future
};

export type EntityTypeMap = {
  user: User;
  case: Case;
};

export type AnswerType = "true" | "false" | string | number | UserUpdate | CaseUpdateInput;

export type Answer = {
  content: AnswerType;
  important?: boolean;
};

export interface CalendarAnswerDTO<PayloadKey extends string> {
  payload: {
    [key in PayloadKey]: Calendar;
  };
}

export interface Questions {
  [key: string]: Question;
}

export type QuestionImportancePolarity = "positive" | "negative";

export type QuestionType = "boolean" | "number" | "string";

export interface Question {
  question: string;
  answer: AnswerType;
  type: QuestionType;
  important: boolean;
  display_in_pdf?: boolean;
  overriden?: boolean;
}

export type PageCategory =
  | "introduction"
  | "user"
  | "trip"
  | "medical"
  | "conditions"
  | "consent"
  | "marketing";

export type InputType =
  | "text"
  | "textarea"
  | "buttons"
  | "date"
  | "autocomplete"
  | "calendar"
  | "radio"
  | "buttons_then_text"
  | "checkbox"
  | "date-range"
  | "component"
  | "automatic";

export type ContextType =
  | "welcomeBack"
  | "addon"
  | "biometrics"
  | "consent"
  | "streamlinedConsultation";
export type InputVariant = "medication-search";

interface Input {
  input: InputType;
  context?: ContextType;
  variant?: InputVariant;
  action?: InputActions;
}

export interface DateRange {
  from: Date | undefined;
  to: Date | undefined;
  duration?: number; // Duration in days
}

type QuestionPageDependencyType = "question" | "conditional";

interface QuestionDependencyType {
  stateKey: string;
  expectedAnswer: string | string[];
}

export interface QuestionnaireContext {
  questions?: Questions;
  isAuth?: boolean;
  partner?: Partner | null;
  query?: URLSearchParams;
}

type ConditionDependencyType =
  | "loggedInUser"
  | "syncStateOfResidence"
  | "dob"
  | "malariaPrevCost"
  | "insurance"
  | "referralSource"
  | "requiresBiometrics"
  | "requiresAddons"
  | "isStreamlinedFlow"
  | "birthControl"
  | "selectedMedicationIsArakoda";

export interface QuestionDependency {
  type: QuestionPageDependencyType;
  question?: QuestionDependencyType;
  conditional?: ConditionDependencyType;
}

type InputActions = "end" | "validate";

export const CUSTOM_OPTION_TITLE = "Other";

export type CustomOptionTitle = "Other";

export interface Option {
  title: string | CustomOptionTitle;
  action?: InputActions;
}

export interface NoticeMessage {
  message: string;
  variant: "error" | "warning" | "success" | "info";
  isInputError?: boolean;
}

export interface WarningMessage extends NoticeMessage {
  trigger?: QuestionDependency;
  messageVariants?: Record<string, NoticeMessage>;
}

export type ActorOverridesPayload = "legalGuardian";
export interface Override {
  title: string;
  dataPoint: string;
}

export interface ActorOverrides {
  payload: ActorOverridesPayload;
  overrides: Override[];
}

export interface StorageMappings<T extends EntityType> {
  // Currently, we only support a single nesting depth.
  nestingProperty?: PropertyKey;
  entityType: T;
  mappings: StoragePath<EntityType>[];
}

export interface Calendar {
  from: string;
  to: string;
  days: number;
}

export interface Page {
  question: string;
  reason?: string;
  noticeMessage?: NoticeMessage;
  warningMessage?: WarningMessage;
  inputType: Input;
  label?: string;
  placeholder?: string;
  options?: Option[];
  format?: string;
  stateKey: string;
  important?: {
    polarity: QuestionImportancePolarity;
  };
  dependsOn?: QuestionDependency;
  storageMappings?: StorageMappings<EntityType>;
  actorOverrides?: ActorOverrides;
  answer?: AnswerType;
  deriveAnswerFromState?: {
    key: string;
    source: "partner"; // Or any other relevant state sources like country or conditions.
  };
}

export type ConditionIndexedPages = {
  [K in ConditionType]: {
    pages: Page[];
  };
};

export type Pages = {
  pages: Page[];
};

export type MergeablePagePath = "otherConditions" | "immunocompromised" | "medicationAllergies";

export interface MergedPage extends Omit<Page, "options"> {
  basePath: string;
  mergedOptions: Set<string>;
  sourceConditions: Map<string, ConditionType>;
}

export interface FlattenedPage extends Page {
  section: PageCategory;
  sectionTitle: string;
  conditionType?: ConditionType;
  parentCondition?: string;
  mergedFrom?: ConditionType[];
}

export type Demographics = "date_of_birth" | "gender";

// Demographics type guard
export const isDemographics = (field: string): field is Demographics => {
  return field === "date_of_birth" || field === "gender";
};

export const isQuestionPageDependencyType = (
  field: string
): field is QuestionPageDependencyType => {
  return field === "question" || field === "conditional";
};

// Type guard QuestionDependency
export const isQuestionDependency = (field: QuestionDependency): field is QuestionDependency => {
  return (
    (field as QuestionDependency)?.type !== undefined && isQuestionPageDependencyType(field?.type)
  );
};

export type QuestionnairePages = {
  introduction: Pages;
  user: Pages;
  trip: Pages;
  medical: Pages;
  conditions: ConditionIndexedPages;
  marketing: Pages;
  consent: Pages;
};
